/**
 * W4 roster synthesis — a pure, auditable present-day population derived from
 * CityGraph geometry and W3 collapsed history.
 *
 * This is deliberately separate from the legacy TownDef NPC runtime. A later W4
 * adapter will expose these records through the existing schedule/navigation API.
 */
import { hashSeed } from "../rng.js";
import { DEFAULT_STYLE, EYE_COLORS, HAIR_COLORS, SKIN_COLORS, type CharacterStyle } from "../rpg.js";
import type { NpcDef, NpcJobKind } from "../npcs.js";
import { generateNpcPersonality, type NpcPersonality } from "../social/personality.js";
import {
  USE_ACADEMY, USE_ADMIN, USE_APOTHECARY, USE_ARENA, USE_BASILICA,
  USE_BATHHOUSE, USE_BUNKHOUSE, USE_CHAPEL, USE_COURTHOUSE, USE_DEPOT,
  USE_DOCKYARD, USE_DWELLING, USE_EMBASSY, USE_FUEL_SHED, USE_GARRISON,
  USE_GUILDHALL, USE_HALL, USE_HARBOR, USE_HEADMAN, USE_INN, USE_LANDING_PAD,
  USE_LOOM_GATE, USE_LOOM_NEXUS, USE_MARKET_HALL, USE_MARKET_SQ, USE_MILL,
  USE_MONUMENT, USE_PALACE, USE_PRECURSOR, USE_PRISON, USE_PRODUCTION,
  USE_SHRINE, USE_SMITHY, USE_STABLE, USE_STARPORT, USE_TEMPLE, USE_THEATER,
  USE_WAREHOUSE, USE_WATCHTOWER, USE_WELL, USE_WORKSHOP,
  type CityGraph,
} from "./citygraph.js";
import type { ChronicleEntry, RegionChronicle, SettlementChronicle } from "./collapse.js";
import { LEVEL_REGION, packCell } from "./cells.js";
import { settlementName } from "./settlementNames.js";
import { namingCulture, personGender, personName } from "./names.js";
import { isParkParcel } from "./structures.js";

export type RosterRole = "keeper" | "trader" | "warden" | "farmer" | "artisan" | "wanderer" | "elder";
export type RosterTrait = "gruff" | "cheerful" | "wary" | "curious" | "weary" | "proud" | "kind" | "sly" | "devout" | "restless";
export type HouseholdKind = "single" | "couple" | "family" | "multigenerational" | "collective";
export type HousingRelaxation = "none" | "shared-dwelling" | "nonresidential" | "unhoused";

export interface FieldProvenance {
  source: "census" | "parcel" | "chronicle" | "household" | "derived";
  ref: string;
}

export interface AgentProvenance {
  identity: FieldProvenance[];
  home: FieldProvenance[];
  age: FieldProvenance[];
  role: FieldProvenance[];
  traits: [FieldProvenance[], FieldProvenance[]];
  wealth: FieldProvenance[];
  relationships: Record<string, FieldProvenance[]>;
}

export interface HouseholdRecord {
  id: string;
  settlementId: string;
  kind: HouseholdKind;
  homeParcel: number | null;
  memberIds: string[];
  relaxation: HousingRelaxation;
}

export interface AgentRecord {
  id: string;
  settlementId: string;
  householdId: string;
  name: string;
  ageYears: number;
  role: RosterRole;
  traits: [RosterTrait, RosterTrait];
  personality: NpcPersonality;
  wealth: number;
  homeParcel: number | null;
  workplaceParcel: number | null;
  bonds: string[];
  grudges: string[];
  provenance: AgentProvenance;
}

export interface SettlementRoster {
  settlementId: string;
  population: number;
  households: HouseholdRecord[];
  agents: AgentRecord[];
  housingCapacity: number;
  /** Household/capacity ratio; null means no usable dwelling geometry exists. */
  housingPressure: number | null;
}

export interface RegionRoster {
  regionId: RegionChronicle["regionId"];
  settlements: SettlementRoster[];
  population: number;
}

export interface ParsedRegionNpcId {
  planetSeed: string;
  regionId: RegionChronicle["regionId"];
  settlementK: number;
  settlementId: string;
  agentIndex: number;
}

/** Parse the stable W4 identity without consulting or materializing the world. */
export function parseRegionNpcId(id: string): ParsedRegionNpcId | null {
  const match = /^(.*):stl:([0-5]):(\d+):(\d+):(\d+):npc:(\d+)$/.exec(id);
  if (!match || match[1].length === 0) return null;
  const face = Number(match[2]), x = Number(match[3]), y = Number(match[4]);
  const settlementK = Number(match[5]), agentIndex = Number(match[6]);
  if (![face, x, y, settlementK, agentIndex].every(Number.isSafeInteger)) return null;
  return {
    planetSeed: match[1], regionId: { face, x, y, level: LEVEL_REGION }, settlementK,
    settlementId: `${match[1]}:stl:${face}:${x}:${y}:${settlementK}`,
    agentIndex,
  };
}

const TRAITS: readonly RosterTrait[] = [
  "gruff", "cheerful", "wary", "curious", "weary", "proud", "kind", "sly", "devout", "restless",
];
function unit(key: string): number { return hashSeed(key) / 4294967296; }
function int(key: string, count: number): number { return count <= 1 ? 0 : Math.floor(unit(key) * count); }
function clamp01(x: number): number { return Math.max(0, Math.min(1, x)); }
function prov(source: FieldProvenance["source"], ref: string): FieldProvenance { return { source, ref }; }

function parcelArea(graph: CityGraph, p: number): number {
  const poly = graph.parcels[p].poly;
  let twice = 0;
  for (let i = 0; i < poly.length; i += 2) {
    const j = (i + 2) % poly.length;
    twice += poly[i] * poly[j + 1] - poly[j] * poly[i + 1];
  }
  return Math.abs(twice) * 0.5;
}

function settlementParcels(settlement: SettlementChronicle): number[] {
  const refs = new Set<number>();
  for (const entry of settlement.entries) {
    if (entry.kind === "district-founded") for (const ref of entry.refs) refs.add(ref);
  }
  return [...refs].sort((a, b) => a - b);
}

const RESIDENTIAL = new Set([USE_DWELLING, USE_BUNKHOUSE, USE_INN, USE_HALL, USE_HEADMAN, USE_PALACE]);
const OPEN = new Set([USE_MARKET_SQ, USE_WELL, USE_MONUMENT, USE_LANDING_PAD, USE_LOOM_GATE]);

/** Number of households whose visible dwelling can credibly fit on a parcel. */
export function parcelHousingCapacity(graph: CityGraph, parcel: number,
  planetHash?: number, rk?: number): number {
  const use = graph.parcels[parcel].use;
  // LANDMARK PARKS (2026-07-31): a park parcel is open ground — nobody lives
  // on it (same isParkParcel rule the structures gate + client layer use).
  if (planetHash !== undefined && rk !== undefined
    && isParkParcel(planetHash, rk, parcel, use)) return 0;
  // Ordinary homes host ONE household (~1.5 residents — singles/couples;
  // 2026-07-31 user call: area-scaled capacity packed 3+ into every house).
  // Communal residences keep area-scaled multi-household capacity.
  if (use !== USE_BUNKHOUSE && use !== USE_INN && use !== USE_PALACE) return 1;
  const area = parcelArea(graph, parcel);
  const density = use === USE_PALACE ? 90 : 55;
  return Math.max(1, Math.min(12, Math.round(area / density)));
}

function householdSize(id: string, remaining: number, prosperity: number): number {
  if (remaining <= 1) return remaining;
  const r = unit(`${id}:size`);
  const p = clamp01(prosperity);
  // Dwellings house 1–2 people: singles + couples only, no larger families.
  // Prosperity nudges toward more singles (wealthier pilots live alone).
  const size = r < 0.45 + 0.1 * p ? 1 : 2;
  return Math.min(size, remaining);
}

function householdKind(id: string, size: number): HouseholdKind {
  if (size === 1) return "single";
  if (size === 2) return unit(`${id}:kind`) < 0.78 ? "couple" : "family";
  if (size >= 5 && unit(`${id}:kind`) < 0.45) return "multigenerational";
  if (unit(`${id}:kind`) < 0.08) return "collective";
  return "family";
}

function memberAgeFor(id: string, kind: HouseholdKind, size: number, member: number): number {
  const head = 27 + int(`${id}:head-age`, 45);
  if (member === 0) return head;
  if (kind === "couple" || (kind === "family" && size >= 3 && member === 1)) {
    return Math.max(18, Math.min(88, head + int(`${id}:partner-gap`, 13) - 6));
  }
  if (kind === "multigenerational" && member === size - 1) {
    return Math.min(92, head + 18 + int(`${id}:elder-gap`, 18));
  }
  if (kind === "collective") return 18 + int(`${id}:peer:${member}`, 48);
  return Math.max(0, head - 18 - int(`${id}:child:${member}`, Math.max(1, head - 17)));
}

function memberAge(household: HouseholdRecord, member: number): number {
  return memberAgeFor(household.id, household.kind, household.memberIds.length, member);
}

const USE_ROLE = new Map<number, RosterRole>([
  [USE_HALL, "keeper"], [USE_HEADMAN, "keeper"], [USE_ADMIN, "keeper"], [USE_COURTHOUSE, "keeper"],
  [USE_PALACE, "keeper"], [USE_EMBASSY, "keeper"], [USE_LOOM_NEXUS, "keeper"],
  [USE_INN, "trader"], [USE_MARKET_HALL, "trader"], [USE_DEPOT, "trader"], [USE_HARBOR, "trader"],
  [USE_GARRISON, "warden"], [USE_WATCHTOWER, "warden"], [USE_PRISON, "warden"], [USE_LOOM_GATE, "warden"],
  [USE_PRODUCTION, "farmer"], [USE_MILL, "farmer"], [USE_STABLE, "farmer"],
  [USE_WORKSHOP, "artisan"], [USE_SMITHY, "artisan"], [USE_WAREHOUSE, "artisan"], [USE_DOCKYARD, "artisan"],
  [USE_FUEL_SHED, "artisan"], [USE_APOTHECARY, "artisan"], [USE_ACADEMY, "artisan"], [USE_STARPORT, "artisan"],
  [USE_GUILDHALL, "artisan"],
  [USE_CHAPEL, "elder"], [USE_TEMPLE, "elder"], [USE_BASILICA, "elder"], [USE_SHRINE, "elder"], [USE_WELL, "elder"],
  [USE_BATHHOUSE, "wanderer"], [USE_THEATER, "wanderer"], [USE_ARENA, "wanderer"],
  [USE_PRECURSOR, "wanderer"], [USE_MONUMENT, "wanderer"], [USE_LANDING_PAD, "wanderer"],
]);

function workSlots(graph: CityGraph, parcels: readonly number[]): { parcel: number; role: RosterRole }[] {
  const slots: { parcel: number; role: RosterRole }[] = [];
  for (const parcel of parcels) {
    const role = USE_ROLE.get(graph.parcels[parcel].use);
    if (role === undefined) continue;
    const count = Math.max(1, Math.min(8, Math.round(parcelArea(graph, parcel) / 120)));
    for (let i = 0; i < count; i++) slots.push({ parcel, role });
  }
  return slots;
}

function fallbackRole(id: string, age: number): RosterRole {
  if (age < 16) return "wanderer";
  if (age >= 68) return "elder";
  const r = unit(`${id}:fallback-role`);
  return r < 0.42 ? "artisan" : r < 0.69 ? "farmer" : r < 0.82 ? "trader" : r < 0.92 ? "warden" : "wanderer";
}

function traitPool(event: ChronicleEntry | undefined): readonly RosterTrait[] {
  if (event?.kind === "war") return ["wary", "proud", "gruff", "restless"];
  if (event?.kind === "plague") return ["weary", "kind", "devout", "wary"];
  if (event?.kind === "boom") return ["cheerful", "curious", "proud", "sly"];
  if (event?.kind === "district-founded") return ["proud", "curious", "restless", "kind"];
  return TRAITS;
}

const HOUSEHOLD_KINDS: readonly HouseholdKind[] = ["single", "couple", "family", "multigenerational", "collective"];
const RELAXATIONS: readonly HousingRelaxation[] = ["none", "shared-dwelling", "nonresidential", "unhoused"];
const ROLES: readonly RosterRole[] = ["keeper", "trader", "warden", "farmer", "artisan", "wanderer", "elder"];
const NO_INDEX = -1;

function parcelCentroid(graph: CityGraph, parcel: number | null): [number, number] {
  if (parcel === null) return [0, 0];
  const poly = graph.parcels[parcel].poly;
  let u = 0, v = 0;
  const count = poly.length / 2;
  for (let i = 0; i < poly.length; i += 2) { u += poly[i]; v += poly[i + 1]; }
  return count > 0 ? [u / count, v / count] : [0, 0];
}

/**
 * Compact settlement census index. Household topology and job allocation live in
 * typed arrays; verbose AgentRecords are created only when queried and retained in
 * a bounded LRU. This is the metro-safe W4 access surface.
 */
export class IndexedSettlementRoster {
  readonly settlementId: string;
  readonly population: number;
  readonly householdCount: number;
  readonly housingCapacity: number;
  readonly housingPressure: number | null;

  private readonly starts: Uint32Array;
  private readonly sizes: Uint8Array;
  private readonly homes: Int32Array;
  private readonly kinds: Uint8Array;
  private readonly relaxations: Uint8Array;
  private readonly workParcels: Int32Array;
  private readonly workRoles: Uint8Array;
  private readonly coworkerPeers: Int32Array;
  private readonly constructionAge = new Map<number, number>();
  private readonly cache = new Map<number, AgentRecord>();
  private readonly cacheLimit: number;
  private readonly adversity: ChronicleEntry | undefined;
  private readonly grudgeHeads: readonly [number, number] | null;

  constructor(
    private readonly graph: CityGraph,
    private readonly settlement: SettlementChronicle,
    cacheLimit = 256,
  ) {
    this.settlementId = settlement.settlementId;
    this.cacheLimit = Math.max(0, Math.floor(cacheLimit));
    const parcels = settlementParcels(settlement);
    const residential = parcels.filter((p) => RESIDENTIAL.has(graph.parcels[p].use));
    const enclosed = parcels.filter((p) => !OPEN.has(graph.parcels[p].use));
    const homes = residential.length > 0 ? residential : enclosed;
    this.population = settlement.populationNow;
    const rosterPlanetHash = hashSeed(namingCulture(this.settlementId));
    const rosterRk = packCell(graph.regionId);
    const capacities = new Map(homes.map((p) => [p, parcelHousingCapacity(graph, p, rosterPlanetHash, rosterRk)]));
    this.housingCapacity = homes.reduce((sum, p) => sum + capacities.get(p)!, 0);
    const homesByMin = [homes, homes, homes.filter((p) => capacities.get(p)! >= 2), homes.filter((p) => capacities.get(p)! >= 3)];

    const starts: number[] = [], sizes: number[] = [], homeRefs: number[] = [];
    const kinds: number[] = [], relaxations: number[] = [];
    const occupancy = new Map<number, number>();
    let remaining = this.population;
    while (remaining > 0) {
      const hi = starts.length;
      const id = `${this.settlementId}:hh:${hi}`;
      const size = householdSize(id, remaining, settlement.prosperityNow);
      const threshold = Math.ceil(size / 2);
      const viable = homesByMin[threshold].filter((p) => (occupancy.get(p) ?? 0) < capacities.get(p)!);
      const available = homes.filter((p) => (occupancy.get(p) ?? 0) < capacities.get(p)!);
      const pool = viable.length > 0 ? viable : available;
      const home = pool.length > 0 ? pool[int(`${id}:home`, pool.length)] : NO_INDEX;
      const existing = home === NO_INDEX ? 0 : (occupancy.get(home) ?? 0);
      const relaxation: HousingRelaxation = home === NO_INDEX ? "unhoused"
        : residential.length === 0 ? "nonresidential"
        : existing > 0 ? "shared-dwelling" : "none";
      starts.push(this.population - remaining); sizes.push(size); homeRefs.push(home);
      kinds.push(HOUSEHOLD_KINDS.indexOf(householdKind(id, size)));
      relaxations.push(RELAXATIONS.indexOf(relaxation));
      if (home !== NO_INDEX) occupancy.set(home, existing + 1);
      remaining -= size;
    }
    this.starts = Uint32Array.from(starts);
    this.sizes = Uint8Array.from(sizes);
    this.homes = Int32Array.from(homeRefs);
    this.kinds = Uint8Array.from(kinds);
    this.relaxations = Uint8Array.from(relaxations);
    this.householdCount = starts.length;
    this.housingPressure = this.housingCapacity > 0
      ? Math.round((this.householdCount / this.housingCapacity) * 1000) / 1000 : null;

    for (const entry of settlement.entries) if (entry.kind === "district-founded") {
      for (const ref of entry.refs) this.constructionAge.set(ref, Math.max(this.constructionAge.get(ref) ?? 0, entry.yearsAgo));
    }

    this.workParcels = new Int32Array(this.population); this.workParcels.fill(NO_INDEX);
    this.workRoles = new Uint8Array(this.population); this.workRoles.fill(255);
    this.coworkerPeers = new Int32Array(this.population); this.coworkerPeers.fill(NO_INDEX);
    const slots = workSlots(graph, parcels);
    const slotUsed = new Uint8Array(slots.length);
    const centres = new Map<number, [number, number]>();
    const centre = (parcel: number | null): [number, number] => {
      if (parcel === null) return [0, 0];
      let found = centres.get(parcel);
      if (!found) { found = parcelCentroid(graph, parcel); centres.set(parcel, found); }
      return found;
    };
    const pendingCoworker = new Map<number, number>();
    let household = 0, work = 0;
    for (let agent = 0; agent < this.population; agent++) {
      while (household + 1 < this.householdCount && agent >= this.starts[household] + this.sizes[household]) household++;
      const age = memberAgeFor(
        `${this.settlementId}:hh:${household}`, HOUSEHOLD_KINDS[this.kinds[household]],
        this.sizes[household], agent - this.starts[household],
      );
      if (age < 16 || work >= slots.length) continue;
      const home = this.homes[household] === NO_INDEX ? null : this.homes[household];
      const [hu, hv] = centre(home);
      let chosen = NO_INDEX, best = Infinity;
      for (let s = 0; s < slots.length; s++) {
        if (slotUsed[s]) continue;
        const [su, sv] = centre(slots[s].parcel);
        const d2 = (su - hu) * (su - hu) + (sv - hv) * (sv - hv);
        if (d2 < best) { best = d2; chosen = s; }
      }
      if (chosen === NO_INDEX) continue;
      slotUsed[chosen] = 1; work++;
      const slot = slots[chosen];
      this.workParcels[agent] = slot.parcel;
      this.workRoles[agent] = ROLES.indexOf(slot.role);
      const peer = pendingCoworker.get(slot.parcel);
      if (peer === undefined) pendingCoworker.set(slot.parcel, agent);
      else {
        this.coworkerPeers[agent] = peer; this.coworkerPeers[peer] = agent;
        pendingCoworker.delete(slot.parcel);
      }
    }

    this.adversity = settlement.entries.find((entry) => entry.kind === "war" || entry.kind === "plague");
    if (this.adversity && this.householdCount > 1) {
      const a = int(`${this.settlementId}:${this.adversity.kind}:grudge:a`, this.householdCount);
      let b = int(`${this.settlementId}:${this.adversity.kind}:grudge:b`, this.householdCount - 1);
      if (b >= a) b++;
      this.grudgeHeads = [this.starts[a], this.starts[b]];
    } else this.grudgeHeads = null;
  }

  get cachedAgentCount(): number { return this.cache.size; }

  /**
   * Bounded identities eligible for local ambient life. Census overflow remains
   * addressable, but does not materialize as hundreds of bodies around the few
   * buildings drawn by the region graph. Housed residents key to their dwelling;
   * unhoused workers may key to their physical workplace; residents with neither
   * visible anchor remain analytic/off-screen.
   */
  localAgentIndices(perParcelLimit = 6): Uint32Array {
    const limit = Math.max(0, Math.floor(perParcelLimit));
    if (limit === 0 || this.population === 0) return new Uint32Array();
    const byParcel = new Map<number, { index: number; score: number }[]>();
    let household = 0;
    for (let agent = 0; agent < this.population; agent++) {
      while (household + 1 < this.householdCount && agent >= this.starts[household] + this.sizes[household]) household++;
      const home = this.homes[household];
      const work = this.workParcels[agent];
      const parcel = home !== NO_INDEX ? home : work;
      if (parcel === NO_INDEX) continue;
      let candidates = byParcel.get(parcel);
      if (!candidates) { candidates = []; byParcel.set(parcel, candidates); }
      candidates.push({ index: agent, score: hashSeed(`${this.settlementId}:local:${parcel}:${agent}`) });
    }
    const selected: number[] = [];
    for (const parcel of [...byParcel.keys()].sort((a, b) => a - b)) {
      const candidates = byParcel.get(parcel)!;
      candidates.sort((a, b) => a.score - b.score || a.index - b.index);
      for (let i = 0; i < Math.min(limit, candidates.length); i++) selected.push(candidates[i].index);
    }
    return Uint32Array.from(selected);
  }

  private householdIndexFor(agent: number): number {
    let lo = 0, hi = this.householdCount - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      if (agent < this.starts[mid]) hi = mid - 1;
      else if (agent >= this.starts[mid] + this.sizes[mid]) lo = mid + 1;
      else return mid;
    }
    throw new RangeError(`agent index ${agent} is outside settlement census`);
  }

  private makeHousehold(index: number): HouseholdRecord {
    if (!Number.isInteger(index) || index < 0 || index >= this.householdCount) throw new RangeError(`household index ${index} is outside settlement census`);
    const start = this.starts[index], size = this.sizes[index];
    return {
      id: `${this.settlementId}:hh:${index}`, settlementId: this.settlementId,
      kind: HOUSEHOLD_KINDS[this.kinds[index]],
      homeParcel: this.homes[index] === NO_INDEX ? null : this.homes[index],
      memberIds: Array.from({ length: size }, (_, member) => `${this.settlementId}:npc:${start + member}`),
      relaxation: RELAXATIONS[this.relaxations[index]],
    };
  }

  householdAt(index: number): HouseholdRecord { return this.makeHousehold(index); }

  private uncachedAgent(index: number): AgentRecord {
    const householdIndex = this.householdIndexFor(index);
    const household = this.makeHousehold(householdIndex);
    const member = index - this.starts[householdIndex];
    const id = `${this.settlementId}:npc:${index}`;
    const ageYears = memberAge(household, member);
    const workplace = this.workParcels[index] === NO_INDEX ? null : this.workParcels[index];
    const role = this.workRoles[index] === 255 ? fallbackRole(id, ageYears) : ROLES[this.workRoles[index]];
    const event = this.settlement.entries[int(`${id}:trait-event`, this.settlement.entries.length)];
    const pool = traitPool(event);
    const t0 = pool[int(`${id}:trait:0`, pool.length)];
    const parcelTraitRef = household.homeParcel === null
      ? `founding@${this.settlement.foundedYearsAgo}`
      : `parcel:${household.homeParcel}:epoch:${this.graph.parcels[household.homeParcel].epoch}`;
    let t1 = TRAITS[int(`${id}:trait:1:${parcelTraitRef}`, TRAITS.length)];
    if (t1 === t0) t1 = TRAITS[(TRAITS.indexOf(t1) + 1) % TRAITS.length];
    const homeAge = household.homeParcel === null ? 0 : (this.constructionAge.get(household.homeParcel) ?? 0);
    const rolePremium = role === "keeper" ? 0.18 : role === "trader" ? 0.1 : role === "artisan" ? 0.04 : 0;
    const wealth = Math.round(clamp01(this.settlement.prosperityNow * 0.65 + rolePremium + unit(`${id}:wealth`) * 0.2
      - (homeAge / Math.max(1, this.settlement.foundedYearsAgo)) * 0.08) * 1000) / 1000;
    const eventRef = event ? `${event.kind}@${event.yearsAgo}` : `founding@${this.settlement.foundedYearsAgo}`;
    const relationships: Record<string, FieldProvenance[]> = {};
    const bonds: string[] = [], grudges: string[] = [];
    const relate = (peerIndex: number, kind: "bond" | "grudge", source: FieldProvenance): void => {
      if (peerIndex === index || peerIndex < 0) return;
      const peer = `${this.settlementId}:npc:${peerIndex}`;
      const list = kind === "bond" ? bonds : grudges;
      if (!list.includes(peer)) list.push(peer);
      const sources = relationships[peer];
      if (sources) sources.push(source); else relationships[peer] = [source];
    };
    if (member > 0) relate(index - 1, "bond", prov("household", household.id));
    if (member + 1 < household.memberIds.length) relate(index + 1, "bond", prov("household", household.id));
    relate(this.coworkerPeers[index], "bond", prov("parcel", `coworkers:parcel:${workplace}`));
    if (this.grudgeHeads && this.adversity) {
      if (index === this.grudgeHeads[0]) relate(this.grudgeHeads[1], "grudge", prov("chronicle", `${this.adversity.kind}@${this.adversity.yearsAgo}`));
      else if (index === this.grudgeHeads[1]) relate(this.grudgeHeads[0], "grudge", prov("chronicle", `${this.adversity.kind}@${this.adversity.yearsAgo}`));
    }
    bonds.sort(); grudges.sort();
    return {
      id, settlementId: this.settlementId, householdId: household.id,
      name: personName(id, household.id, this.settlementId, personGender(id)), ageYears, role, traits: [t0, t1], wealth,
      personality: generateNpcPersonality({ identity: ["region", id], traits: [t0, t1],
        communityRef: `${this.settlementId}:${eventRef}` }),
      homeParcel: household.homeParcel, workplaceParcel: workplace, bonds, grudges,
      provenance: {
        identity: [prov("census", `${this.settlementId}:population=${this.population}`)],
        home: [prov(household.homeParcel === null ? "census" : "parcel", household.homeParcel === null ? "housing-relaxation:unhoused" : `parcel:${household.homeParcel}`)],
        age: [prov("household", `${household.id}:${household.kind}:member:${member}`)],
        role: [prov(workplace === null ? "derived" : "parcel", workplace === null ? `${this.settlementId}:demographic-fallback` : `parcel:${workplace}:use:${this.graph.parcels[workplace].use}`)],
        traits: [[prov("chronicle", eventRef)], [prov(household.homeParcel === null ? "chronicle" : "parcel", parcelTraitRef)]],
        wealth: [prov("chronicle", `prosperity:${this.settlement.prosperityNow}`), prov("household", household.id)],
        relationships,
      },
    };
  }

  agentAt(index: number): AgentRecord {
    if (!Number.isInteger(index) || index < 0 || index >= this.population) throw new RangeError(`agent index ${index} is outside settlement census`);
    const cached = this.cache.get(index);
    if (cached) { this.cache.delete(index); this.cache.set(index, cached); return cached; }
    const agent = this.uncachedAgent(index);
    if (this.cacheLimit > 0) {
      this.cache.set(index, agent);
      if (this.cache.size > this.cacheLimit) this.cache.delete(this.cache.keys().next().value!);
    }
    return agent;
  }

  agentById(id: string): AgentRecord | null {
    const prefix = `${this.settlementId}:npc:`;
    if (!id.startsWith(prefix)) return null;
    const index = Number(id.slice(prefix.length));
    return Number.isInteger(index) && index >= 0 && index < this.population ? this.agentAt(index) : null;
  }

  agentRange(start: number, count: number): AgentRecord[] {
    if (!Number.isInteger(start) || !Number.isInteger(count) || start < 0 || count < 0 || start + count > this.population) {
      throw new RangeError("agent range is outside settlement census");
    }
    return Array.from({ length: count }, (_, offset) => this.agentAt(start + offset));
  }

  materialize(): SettlementRoster {
    return {
      settlementId: this.settlementId, population: this.population,
      households: Array.from({ length: this.householdCount }, (_, i) => this.householdAt(i)),
      agents: this.agentRange(0, this.population), housingCapacity: this.housingCapacity,
      housingPressure: this.housingPressure,
    };
  }
}

export interface IndexedRegionRoster {
  regionId: RegionChronicle["regionId"];
  settlements: IndexedSettlementRoster[];
  population: number;
  agentById(id: string): AgentRecord | null;
  materialize(): RegionRoster;
}

/** Create the compact runtime roster index without materializing resident objects. */
export function indexRegionRoster(graph: CityGraph, chronicle: RegionChronicle, cacheLimit = 256): IndexedRegionRoster {
  const sameRegion = graph.regionId.face === chronicle.regionId.face
    && graph.regionId.x === chronicle.regionId.x && graph.regionId.y === chronicle.regionId.y
    && graph.regionId.level === chronicle.regionId.level;
  if (!sameRegion) throw new Error("indexRegionRoster: graph and chronicle regions differ");
  const settlements = chronicle.settlements.map((settlement) => new IndexedSettlementRoster(graph, settlement, cacheLimit));
  const byPrefix = [...settlements].sort((a, b) => b.settlementId.length - a.settlementId.length);
  const indexed: IndexedRegionRoster = {
    regionId: { ...chronicle.regionId }, settlements,
    population: settlements.reduce((sum, settlement) => sum + settlement.population, 0),
    agentById(id: string): AgentRecord | null {
      const settlement = byPrefix.find((candidate) => id.startsWith(`${candidate.settlementId}:npc:`));
      return settlement?.agentById(id) ?? null;
    },
    materialize(): RegionRoster {
      return {
        regionId: { ...chronicle.regionId },
        settlements: settlements.map((settlement) => settlement.materialize()),
        population: indexed.population,
      };
    },
  };
  return indexed;
}

/** Derive the verbose audit form. Runtime consumers should prefer indexRegionRoster. */
export function deriveRegionRoster(graph: CityGraph, chronicle: RegionChronicle): RegionRoster {
  return indexRegionRoster(graph, chronicle).materialize();
}

function historyYears(yearsAgo: number): string {
  const years = Math.max(1, Math.round(yearsAgo));
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

/** Player-facing facts selected from the exact chronicle records that shaped
 *  this resident's traits and home. This is presentation derived from facts,
 *  not new world state. */
export function agentHistoryLines(agent: AgentRecord, settlement: SettlementChronicle): string[] {
  const name = settlementName(settlement.settlementId);
  const lines: string[] = [];
  const add = (entry: ChronicleEntry | undefined): void => {
    if (!entry) return;
    let line: string;
    switch (entry.kind) {
      case "war": line = `The war reshaped ${name} ${historyYears(entry.yearsAgo)}. Some families still carry the scars.`; break;
      case "plague": line = `The plague reached ${name} ${historyYears(entry.yearsAgo)}. We still keep its names.`; break;
      case "boom": line = `${name} prospered in the boom ${historyYears(entry.yearsAgo)}. Half these streets date from it.`; break;
      case "district-founded": line = `This quarter of ${name} took shape ${historyYears(entry.yearsAgo)}. The old walls remember.`; break;
      default: line = `${name} was founded ${historyYears(entry.yearsAgo)}. The oldest stones still show it.`; break;
    }
    if (!lines.includes(line)) lines.push(line);
  };

  const traitRef = agent.provenance.traits.flat().find((p) => p.source === "chronicle")?.ref;
  if (traitRef) {
    const split = traitRef.lastIndexOf("@");
    const kind = split < 0 ? traitRef : traitRef.slice(0, split);
    const years = split < 0 ? NaN : Number(traitRef.slice(split + 1));
    add(settlement.entries.find((entry) => entry.kind === kind
      && (!Number.isFinite(years) || Math.abs(entry.yearsAgo - years) < 1e-6)));
  }
  if (agent.homeParcel !== null) {
    add(settlement.entries.find((entry) => entry.kind === "district-founded" && entry.refs.includes(agent.homeParcel!)));
  }
  add(settlement.entries.find((entry) => entry.kind === "founding"));
  return lines.slice(0, 3);
}

function regionStyle(agent: AgentRecord): CharacterStyle {
  const style: CharacterStyle = {
    ...DEFAULT_STYLE,
    // Wayfarer NPCs (2026-07-31): body follows the SAME id-derived gender rule
    // the roster's personName used, so name and body always agree.
    body: personGender(agent.id) === "f" ? "female" : "male",
    suit: hashSeed(`${agent.id}:style:suit`) & 0xffffff,
    accent: hashSeed(`${agent.id}:style:accent`) & 0xffffff,
    // Appearance colour axes (2026-07-31): skin / hair / eye from the shared decks.
    skin: SKIN_COLORS[hashSeed(`${agent.id}:style:skin`) % SKIN_COLORS.length],
    hairColor: HAIR_COLORS[hashSeed(`${agent.id}:style:hairc`) % HAIR_COLORS.length],
    eyeColor: EYE_COLORS[hashSeed(`${agent.id}:style:eyec`) % EYE_COLORS.length],
  };
  // Belts and packs belong to the legs and torso; wardens add arm guards.
  style.torso = "drifter"; style.legs = "drifter"; style.feet = "drifter";
  if (agent.role === "warden") style.arms = "drifter";
  return style;
}

const ROLE_JOB: Readonly<Record<RosterRole, NpcJobKind>> = {
  keeper: "hall", trader: "stall", warden: "patrol", farmer: "field",
  artisan: "shopfront", elder: "well", wanderer: "none",
};

/**
 * Project a W4 resident into the established identity/dialogue persona contract.
 * Region parcel anchors remain explicit; legacy TownDef schedule code must not
 * interpret `home: -1` as a building index.
 */
export function regionNpcDef(
  graph: CityGraph,
  agent: AgentRecord,
  chronicle?: RegionChronicle | SettlementChronicle,
): NpcDef {
  const anchor = agent.workplaceParcel ?? agent.homeParcel;
  const [u, v] = parcelCentroid(graph, anchor);
  const settlement = chronicle && "settlements" in chronicle
    ? chronicle.settlements.find((candidate) => candidate.settlementId === agent.settlementId)
    : chronicle;
  return {
    id: agent.id, name: agent.name, role: agent.role, style: regionStyle(agent),
    traits: agent.traits, u, v, roam: agent.role === "wanderer" ? 18 : agent.workplaceParcel === null ? 6 : 3,
    personality: agent.personality,
    home: -1, job: ROLE_JOB[agent.role], jobData: [u, v, u, v], source: "region",
    regionHomeParcel: agent.homeParcel, regionWorkplaceParcel: agent.workplaceParcel,
    history: settlement ? agentHistoryLines(agent, settlement) : undefined,
  };
}

/** Bounded deterministic personas used by settlement boards and target resolution. */
export function regionQuestRoster(graph: CityGraph, settlement: IndexedSettlementRoster): NpcDef[] {
  const indices = [...settlement.localAgentIndices(6)];
  if (indices.length === 0 && settlement.population > 0) indices.push(0);
  return indices.map((index) => regionNpcDef(graph, settlement.agentAt(index)));
}
